import { useCallback } from 'react';
import {
  DropdownListItem,
  DropdownListItemEmpty,
} from '@components/elements/DropdownList';
import { fetchActiveJobsByWorker } from '@company/services/job/api';

/**
 * Utility hook for processing active jobs into shareable dropdown items.
 * Filters out jobs that can't be shared and formats data for UI.
 * Use this when you need to show a dropdown of jobs to share with a worker.
 *
 * @returns Object containing getActiveJobsForWorker function
 */
export function useActiveJobsForWorker() {
  const getActiveJobsForWorker = useCallback(
    async (
      workerId: number,
      shareJobToWorker: (jobId: number, workerId: number) => Promise<void>
    ): Promise<DropdownListItem[] | DropdownListItemEmpty> => {
      try {
        const activeJobsData = await fetchActiveJobsByWorker(workerId);

        if (!activeJobsData?.results || activeJobsData.results.length === 0) {
          return {
            label: 'No published jobs to share',
          } as DropdownListItemEmpty;
        }

        // Filter out jobs that can't be shared (already have jobAssignment)
        const shareableJobs = activeJobsData.results.filter(
          (jobData: any) => !jobData.jobAssignment
        );

        if (shareableJobs.length === 0) {
          return {
            label: 'No available jobs to share',
          } as DropdownListItemEmpty;
        }

        return shareableJobs.map(({ job }: any) => ({
          header: job.eventName,
          label: `${job.position} - ${job.address?.city}/${
            job.address?.state.ansiAbbreviation ?? ''
          }`,
          key: `${job.position} ${job.address?.city} ${
            job.address?.state.ansiAbbreviation ?? ''
          }`,
          id: job.id,
          action: () => shareJobToWorker(job.id, workerId),
        })) as DropdownListItem[];
      } catch (e) {
        console.error('Cannot fetch active jobs', e);
        return Promise.reject(new Error('Cannot fetch active jobs'));
      }
    },
    []
  );

  return { getActiveJobsForWorker };
}
